import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faForward } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useBodyLock } from '@sorare/core/src/hooks/useBodyLock';

import { SlideIntoCollection } from './SlideIntoCollection';
import type { CollectionAnimation_anyCardInterface } from './__generated__/index.graphql';

const Wrapper = styled.div`
  overflow: hidden;
  position: relative;
`;

const InnerWrapper = styled(Horizontal)`
  transition: transform 1.1s cubic-bezier(0.04, 0.61, 0.37, 0.97);
`;

const SkipButton = styled(IconButton)`
  position: absolute;
  bottom: var(--double-unit);
  right: var(--triple-unit);
`;

type Props = {
  newCards: CollectionAnimation_anyCardInterface[];
  onAnimationEnd: () => void;
  shouldAnimate: boolean;
};

export const CollectionAnimation = ({
  newCards,
  onAnimationEnd: onAnimationEndProp,
  shouldAnimate,
}: Props) => {
  useBodyLock(true);
  const [currentCollectionIndex, setCurrentCollectionIndex] =
    useState<number>();

  const cardCollectionSlugs = [
    ...new Set(
      newCards
        .flatMap(card => card.cardCollectionCards)
        .map(card => card.cardCollection.slug)
    ),
  ];

  useEffect(() => {
    if (shouldAnimate) {
      setCurrentCollectionIndex(0);
    }
  }, [shouldAnimate]);

  const onAnimationEnd = () => {
    if (typeof currentCollectionIndex !== 'number') {
      return;
    }
    if (currentCollectionIndex === cardCollectionSlugs.length - 1) {
      onAnimationEndProp();
      return;
    }
    setCurrentCollectionIndex(currentCollectionIndex + 1);
  };

  return (
    <>
      <Wrapper>
        <InnerWrapper
          style={{
            transform: `translateX(calc(-100% * ${currentCollectionIndex})`,
          }}
          gap={0}
        >
          {cardCollectionSlugs.map((cardCollectionSlug, index) => (
            <SlideIntoCollection
              key={cardCollectionSlug}
              cardCollectionSlug={cardCollectionSlug}
              newCards={newCards.filter(card =>
                card.cardCollectionCards.some(
                  ccc => ccc.cardCollection.slug === cardCollectionSlug
                )
              )}
              shouldAnimate={index === currentCollectionIndex}
              onAnimationEnd={onAnimationEnd}
            />
          ))}
        </InnerWrapper>
      </Wrapper>
      <SkipButton
        size="medium"
        onClick={() => {
          onAnimationEndProp();
        }}
        color="tertiary"
        disableDebounce
        icon={faForward}
      />
    </>
  );
};

CollectionAnimation.fragments = {
  anyCardInterface: gql`
    fragment CollectionAnimation_anyCardInterface on AnyCardInterface {
      slug
      cardCollectionCards {
        id
        cardCollection {
          slug
        }
      }
      ...SlideIntoCollection_anyCardInterface
    }
    ${SlideIntoCollection.fragments.anyCardInterface}
  ` as TypedDocumentNode<CollectionAnimation_anyCardInterface>,
};
